import { useCallback, useMemo } from 'react';
import { alwaysArray } from '@helpers/data';
import { joinWith } from '@helpers/format';
import { BaseSelectInputBaseInputStyled, BaseSelectInputSelectStyled, BaseSelectInputStyled } from './styled';
import { BaseSelectInputOption, BaseSelectInputProps, BaseSelectInputValue } from './types';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
export const BaseSelectInput = <V extends BaseSelectInputValue,>(props: BaseSelectInputProps<V>): React.ReactElement<BaseSelectInputProps<V>> => {
  const {
    className,
    inputClassName,
    iconClassName,
    value: originValue,
    options: originOptions,
    multiple,
    icon,
    hideValue,
    readOnly,
    disabled,
    hasError,
    errorText,
    selectRef,
    onFocus,
    onBlur,
    onChange,
    ...restProps
  } = props;
  const value = useMemo(() => {
    if (Array.isArray(originValue)) {
      return Array.from(originValue).map(String);
    }
    return (originValue as number | string | undefined);
  }, [originValue]);
  const emptyOption = useMemo(() => {
    return ({
      value: undefined,
      label: ''
    } as BaseSelectInputOption<V>);
  }, []);
  const options = useMemo(() => {
    if (multiple) {
      return originOptions;
    }
    const hasEmpty = originOptions.some(option => option.value == undefined);
    if (hasEmpty) {
      return originOptions;
    }
    return [emptyOption, ...originOptions];
  }, [emptyOption, originOptions, multiple]);
  const selectedOptions = useMemo(() => {
    const _value = originValue !== undefined //
    ? alwaysArray<V>(originValue) : [];
    return options.filter(option => {
      return _value.includes(option.value);
    });
  }, [originValue, options]);
  const textValue = useMemo(() => {
    if (hideValue) {
      return '';
    }
    return joinWith(selectedOptions.map(selectedOption => selectedOption.label), ', ');
  }, [hideValue, selectedOptions]);
  const change = useCallback((event: React.ChangeEvent<HTMLSelectElement>) => {
    const {
      selectedOptions
    } = event.target;
    const selectedOptionValues = Array.from(selectedOptions) //
    .map(selectedOption => selectedOption.value);
    const optionValues = options //
    .map(option => option.value);
    const value = optionValues.filter(optionValue => {
      return selectedOptionValues.some(selectedOptionValue => String(selectedOptionValue ?? '') === String(optionValue ?? ''));
    });
    const nextValue = multiple ? value : value[0];
    onChange?.(nextValue, event);
  }, [options, multiple, onChange]);
  return _jsxs(BaseSelectInputStyled, {
    className: className,
    children: [_jsx(BaseSelectInputBaseInputStyled //
    , {
      inputClassName: inputClassName,
      iconClassName: iconClassName,
      value: textValue,
      icon: icon,
      readOnly: true,
      disabled: disabled,
      hasError: hasError,
      errorText: errorText,
      onFocus: onFocus,
      onBlur: onBlur,
      ...restProps
    }), _jsx(BaseSelectInputSelectStyled //
    , {
      ref: selectRef,
      value: value ?? '',
      multiple: multiple,
      disabled: readOnly ?? disabled,
      onChange: change,
      ...restProps,
      children: options.map(option => {
        return _jsx("option", {
          value: option.value ?? '',
          children: option.label
        }, String(option.value));
      })
    })]
  });
};